import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighchartsExporting from 'highcharts/modules/exporting';
import isEmpty from 'lodash/isEmpty';
import { CampaignAnalyticsPieChart } from 'modules/campaignAnalytics/components/CampaignAnalyticsPieChart';
import { StyledDivider, TabularNumberFont } from 'modules/productAnalytics/Styled';
import {
  formatDeviceData,
  getChartActions,
  getChartExportingOptions,
} from 'modules/productAnalytics/utils';
import { FC, useMemo, useRef } from 'react';
import styled from 'styled-components';
import { BUTTON_VARIANTS, IconButton } from 'ui/button';
import { Overflow } from 'ui/icons';
import { NoResult } from 'ui/illustrations';
import { Menu } from 'ui/menu';
import { ALIGNMENT } from 'ui/menu/constants';
import { Skeleton } from 'ui/skeleton';
import { body1Bold, body2, colors, spacing } from 'ui/theme';
import { Container } from '../Styled';
import { Devices as DevicesSvg } from '../assets/devices';
import { useRestData } from '../hooks/useRestData';
import { RestDataIdentifier } from '../types';
import {
  EmptyStateContainer,
  EmptyStateSubtitle,
  EmptyStateTitle,
  NoDataMessage,
} from './NoDataMessage';

export const MetricList = styled.div`
  padding: ${spacing.half} ${spacing.plusHalf};
  &:nth-child(odd) {
    background: ${colors.backgroundPrimary};
  }
  &:nth-child(even) {
    background: ${colors.backgroundSecondary};
  }
`;

export const MetricRow = styled.div`
  ${body2};
  display: flex;
  justify-content: space-between;
`;

const ChartHeader = styled.div`
  display: flex;
  justify-content: space-between;
  width: 100%;
  background: ${colors.white};
  padding: ${spacing.base} ${spacing.plusHalf};
  border-bottom: ${spacing.single} solid ${colors.grey15};
  margin-bottom: ${spacing.plusHalf};
`;

const ChartTitle = styled.div`
  ${body1Bold}
  display: flex;
  align-items: center;
`;

const DeviceContainer = styled(Container)`
  overflow: hidden;
  padding-bottom: 0;
`;

const DeviceTableWrapper = styled.div`
  padding-top: ${spacing.base};
  padding-bottom: ${spacing.base};
`;

if (typeof Highcharts === 'object') {
  HighchartsExporting(Highcharts);
}

type DeviceData = { [device: string]: number };

export const Devices: FC<React.PropsWithChildren<unknown>> = () => {
  const {
    data: deviceBucketMetrics,
    loading,
    error: showErrorState,
  } = useRestData(RestDataIdentifier.DEVICE);

  const chartRef = useRef<HighchartsReact>(null);
  const chartId = 'devicePieChart';
  const chartActions = getChartActions(chartId, chartRef);

  const { deviceData: memoizedDeviceData, formattedData: memoizedFormattedData } = useMemo(() => {
    const deviceData = (deviceBucketMetrics?.value || {}) as DeviceData;
    const formattedData = formatDeviceData(deviceData);
    return { deviceData, formattedData };
  }, [deviceBucketMetrics]);

  return (
    <>
      {!showErrorState && !loading && !isEmpty(deviceBucketMetrics) && (
        <DeviceContainer>
          <div>
            <ChartHeader>
              <ChartTitle>Device type breakdown</ChartTitle>
              <Menu
                options={getChartExportingOptions(chartId)}
                handleSelectionChange={(key: string) => chartActions[key]()}
                horizontalAlignment={ALIGNMENT.RIGHT}
              >
                <IconButton variant={BUTTON_VARIANTS.SECONDARY}>
                  <Overflow />
                </IconButton>
              </Menu>
            </ChartHeader>
          </div>
          <CampaignAnalyticsPieChart data={memoizedFormattedData} ref={chartRef} />
          <StyledDivider />
          <DeviceTableWrapper>
            {Object.keys(memoizedDeviceData)
              ?.sort(
                (deviceA, deviceB) => memoizedDeviceData[deviceB] - memoizedDeviceData[deviceA],
              )
              ?.map((key, idx) => (
                <MetricList key={`metric-row-${idx}`}>
                  <MetricRow>
                    <span>{key === '-' ? 'Unknown' : key}</span>
                    {typeof memoizedDeviceData[key] === 'number' ? (
                      <TabularNumberFont>
                        {memoizedDeviceData[key].toLocaleString()}
                      </TabularNumberFont>
                    ) : (
                      <span>{memoizedDeviceData[key]}</span>
                    )}
                  </MetricRow>
                </MetricList>
              ))}
          </DeviceTableWrapper>
        </DeviceContainer>
      )}
      {!showErrorState && loading && (
        <DeviceContainer>
          <Skeleton height={500} />
        </DeviceContainer>
      )}
      {isEmpty(deviceBucketMetrics) && !showErrorState && !loading && (
        <EmptyStateContainer>
          <DevicesSvg />
          <EmptyStateTitle>No device data available</EmptyStateTitle>
          <EmptyStateSubtitle>
            Device type breakdown is only available for email campaigns
          </EmptyStateSubtitle>
        </EmptyStateContainer>
      )}
      {showErrorState && !loading && (
        <NoDataMessage
          Icon={NoResult}
          title="Sorry we couldn't load this page"
          subtitle="Try refreshing the page. If this keeps happening, let us know."
        />
      )}
    </>
  );
};
